import {
  type BuiltLine,
  type FractionLabel,
  type Jump,
  pointLetter,
  positionOf,
  type Rational,
  type TickLabel,
} from "@/lib/tool-kit/worksheets/numberline";

const INK = "#171717";
const LEFT = 50;
const WIDTH = 900;
const FONT = "ui-sans-serif, system-ui, sans-serif";
const MIN_FONT = 9;
const MAX_FONT = 22;
const JUMP_AREA = 84;
const LETTER_AREA = 26;
const LABEL_AREA = 92;
const TOP_PAD = 14;

const round = (value: number) => Math.round(value * 100) / 100;
const xAt = (fraction: number) => round(LEFT + fraction * WIDTH);

function labelChars(label: TickLabel): number {
  if (label.kind === "plain") {
    return label.text.length;
  }
  const { sign, whole, n, d } = label.label;
  return (
    sign.length + (whole ? whole.length + 1 : 0) + Math.max(n.length, d.length)
  );
}

/** A label size that fits the room between labeled ticks. */
function fontFor(line: BuiltLine): number {
  const labeled = line.ticks.filter((t) => t.labeled);
  const gaps = labeled
    .slice(1)
    .map((t, i) => (t.fraction - (labeled[i]?.fraction ?? 0)) * WIDTH);
  const room = Math.min(...gaps, WIDTH);
  const chars = Math.max(1, ...labeled.map((t) => labelChars(t.label)));
  return Math.max(MIN_FONT, Math.min(MAX_FONT, (room * 0.86) / (chars * 0.62)));
}

function Stacked({
  label,
  x,
  y,
  size,
}: {
  label: FractionLabel;
  x: number;
  y: number;
  size: number;
}) {
  const bar = Math.max(label.n.length, label.d.length) * size * 0.34 + 3;
  const wholeText = `${label.sign}${label.whole ?? ""}`;
  return (
    <g fill={INK} fontFamily={FONT} fontSize={size} textAnchor="middle">
      {wholeText ? (
        <text
          dominantBaseline="central"
          textAnchor="end"
          x={round(x - bar - 3)}
          y={round(y + size * 0.55)}
        >
          {wholeText}
        </text>
      ) : null}
      <text x={x} y={round(y + size * 0.1)}>
        {label.n}
      </text>
      <line
        stroke={INK}
        strokeWidth={1.4}
        x1={round(x - bar)}
        x2={round(x + bar)}
        y1={round(y + size * 0.3)}
        y2={round(y + size * 0.3)}
      />
      <text x={x} y={round(y + size * 1.25)}>
        {label.d}
      </text>
    </g>
  );
}

function Arrowhead({
  x,
  y,
  dx,
  dy,
}: {
  x: number;
  y: number;
  dx: number;
  dy: number;
}) {
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  const wing = (sign: number) => {
    const angle = (sign * 28 * Math.PI) / 180;
    const bx = -(ux * Math.cos(angle) - uy * Math.sin(angle));
    const by = -(ux * Math.sin(angle) + uy * Math.cos(angle));
    return `${round(x + bx * 11)},${round(y + by * 11)}`;
  };
  return (
    <polyline
      fill="none"
      points={`${wing(1)} ${round(x)},${round(y)} ${wing(-1)}`}
      stroke={INK}
      strokeLinecap="round"
      strokeWidth={2.5}
    />
  );
}

/** One number line as SVG: ticks, labels (or boxes for hidden ones), jumps and points. */
export function NumberLineSvg({
  line,
  hidden,
  jumps,
  points,
  letters,
  arrows,
  label,
}: {
  line: BuiltLine;
  hidden: ReadonlySet<number>;
  jumps: readonly Jump[];
  points: readonly Rational[];
  letters: boolean;
  arrows: boolean;
  label: string;
}) {
  const size = fontFor(line);
  const lettersShown = letters && points.length > 0;
  const base =
    TOP_PAD +
    (jumps.length > 0 ? JUMP_AREA : 0) +
    (lettersShown ? LETTER_AREA : 0) +
    8;
  const height = base + LABEL_AREA;
  const lineStart = arrows ? 24 : LEFT;
  const lineEnd = arrows ? 976 : LEFT + WIDTH;
  return (
    <svg
      aria-label={label}
      className="block h-auto w-full"
      role="img"
      viewBox={`0 0 1000 ${height}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <line
        stroke={INK}
        strokeLinecap="round"
        strokeWidth={3}
        x1={lineStart}
        x2={lineEnd}
        y1={base}
        y2={base}
      />
      {arrows ? (
        <>
          <Arrowhead dx={-1} dy={0} x={lineStart} y={base} />
          <Arrowhead dx={1} dy={0} x={lineEnd} y={base} />
        </>
      ) : null}
      {line.ticks.map((tick) => {
        const x = xAt(tick.fraction);
        const half = tick.labeled ? 10 : 6;
        const boxWidth = Math.max(30, size * 2.4);
        return (
          <g key={`tick-${tick.index}`}>
            <line
              stroke={INK}
              strokeWidth={tick.labeled ? 2.2 : 1.4}
              x1={x}
              x2={x}
              y1={base - half}
              y2={base + half}
            />
            {tick.labeled && hidden.has(tick.index) ? (
              <rect
                fill="#ffffff"
                height={round(size * 1.6)}
                rx={3}
                stroke={INK}
                strokeWidth={1.4}
                width={round(boxWidth)}
                x={round(x - boxWidth / 2)}
                y={base + 18}
              />
            ) : null}
            {tick.labeled &&
            !hidden.has(tick.index) &&
            tick.label.kind === "plain" ? (
              <text
                dominantBaseline="hanging"
                fill={INK}
                fontFamily={FONT}
                fontSize={round(size)}
                textAnchor="middle"
                x={x}
                y={base + 18}
              >
                {tick.label.text}
              </text>
            ) : null}
            {tick.labeled &&
            !hidden.has(tick.index) &&
            tick.label.kind === "fraction" ? (
              <Stacked
                label={tick.label.label}
                size={round(size)}
                x={x}
                y={base + 28}
              />
            ) : null}
          </g>
        );
      })}
      {jumps.map((jump, index) => {
        const fromX = xAt(positionOf(jump.from, line.spec));
        const toX = xAt(positionOf(jump.to, line.spec));
        const reach = Math.abs(toX - fromX) / WIDTH;
        const rise = round(18 + 52 * Math.min(1, reach * 1.6));
        const mid = round((fromX + toX) / 2);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: jumps are positional
          <g key={`jump-${index}`}>
            <path
              d={`M${fromX} ${base} Q${mid} ${round(base - 2 * rise)} ${toX} ${base}`}
              fill="none"
              stroke={INK}
              strokeWidth={2.5}
            />
            <Arrowhead dx={toX - mid} dy={2 * rise} x={toX} y={base} />
            <text
              fill={INK}
              fontFamily={FONT}
              fontSize={20}
              fontWeight={600}
              textAnchor="middle"
              x={mid}
              y={round(base - rise - 9)}
            >
              {jump.label}
            </text>
          </g>
        );
      })}
      {points.map((point, index) => {
        const x = xAt(positionOf(point, line.spec));
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: points are positional
          <g key={`point-${index}`}>
            <circle cx={x} cy={base} fill={INK} r={8} />
            {lettersShown ? (
              <text
                fill={INK}
                fontFamily={FONT}
                fontSize={22}
                fontWeight={700}
                textAnchor="middle"
                x={x}
                y={base - 18}
              >
                {pointLetter(index)}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
